<template>
  <CommonPages id="detail" :class="['detail']" :titlePrefix="bookInfo.title || 'Detail'"
    :metaDescription="bookInfo.summary || domainConfig?.['meta']?.des">

    <div class="detail-container">
      <!-- 书籍基本信息部分 -->
      <div class="detail__top-card">
        <div class="detail__cover">
          <img v-if="!loading" :src="bookInfo.cover" alt="Book Cover" class="detail__cover-img"
            @click="startReading">
          <div v-else class="detail__cover--skeleton"></div>
        </div>
        <div class="detail__info">
          <div class="detail__info-header">
            <div class="detail__info-author">
              <template v-if="!loading">By {{ bookInfo.author }}</template>
            </div>

            <AdComponent v-if="deviceType == 'mobile'"
              v-show="$global.isObjectNotEmpty(adsensConfig) && adsensConfig?.detail_1"
              ref="ads-detail-1" class="pc_padding" :ads="adsensConfig?.detail_1"
              :showDebug="showDebug" />

            <h1 class="detail__info-title">
              <template v-if="!loading">{{ bookInfo.title }}</template>
            </h1>
          </div>
          <!-- PC layout: single row -->
          <div v-if="deviceType !== 'mobile'" class="detail__info-meta">
            <div class="meta-item">
              <span class="meta-item__label">Score</span>
              <div class="meta-item__value">
                <img src="@/assets/star.svg" alt="score">
                <span>{{ bookInfo.score }}</span>
              </div>
            </div>
            <div class="meta-item">
              <span class="meta-item__label">Category</span>
              <div class="meta-item__value"><span>{{ bookInfo.category }}</span></div>
            </div>
            <div class="meta-item">
              <span class="meta-item__label">Update</span>
              <div class="meta-item__value"><span>{{ bookInfo.updateTime }}</span></div>
            </div>
            <div class="meta-item">
              <span class="meta-item__label">Chapters</span>
              <div class="meta-item__value"><span>{{ bookInfo.chapters }}</span></div>
            </div>
            <div class="meta-item">
              <span class="meta-item__label">Views</span>
              <div class="meta-item__value"><span>{{ bookInfo.views }}</span></div>
            </div>
            <div class="meta-item">
              <span class="meta-item__label">Marked</span>
              <div class="meta-item__value"><span>{{ bookInfo.marked }}</span></div>
            </div>
          </div>
          <!-- Mobile layout: two rows -->
          <div v-else class="detail__info-meta-container">
            <div class="detail__info-meta">
              <div class="meta-item">
                <span class="meta-item__label">Score</span>
                <div class="meta-item__value">
                  <img src="@/assets/star.svg" alt="score">
                  <span>{{ bookInfo.score }}</span>
                </div>
              </div>
              <div class="meta-item">
                <span class="meta-item__label">Category</span>
                <div class="meta-item__value"><span>{{ bookInfo.category }}</span></div>
              </div>
              <div class="meta-item">
                <span class="meta-item__label">Update</span>
                <div class="meta-item__value"><span>{{ bookInfo.updateTime }}</span></div>
              </div>
            </div>
            <div class="meta-separator" v-if="!loading"></div>
            <div class="detail__info-meta">
              <div class="meta-item">
                <span class="meta-item__label">Chapters</span>
                <div class="meta-item__value"><span>{{ bookInfo.chapters }}</span></div>
              </div>
              <div class="meta-item">
                <span class="meta-item__label">Views</span>
                <div class="meta-item__value"><span>{{ bookInfo.views }}</span></div>
              </div>
              <div class="meta-item">
                <span class="meta-item__label">Marked</span>
                <div class="meta-item__value"><span>{{ bookInfo.marked }}</span></div>
              </div>
            </div>
          </div>
          <div class="detail__info-cta" @click="startReading">
            <img src="@/assets/start.svg" alt="Start">
            <span>Start Reading</span>
          </div>
        </div>
      </div>

      <AdComponent v-if="deviceType != 'mobile'"
        v-show="$global.isObjectNotEmpty(adsensConfig) && adsensConfig?.detail_1" ref="ads-detail-1"
        class="ad-width-100" :ads="adsensConfig?.detail_1" :showDebug="showDebug" />

      <!-- 摘要和标签部分 -->
      <div class="detail__bottom-card">
        <div class="detail__summary">
          <h2 class="detail__section-title">Summary</h2>
          <p class="detail__summary-content">
            <template v-if="!loading">{{ bookInfo.summary }}</template>
          </p>
        </div>
        <div class="detail__tags" v-if="!loading && bookInfo.tags && bookInfo.tags.length > 0">
          <h2 class="detail__section-title">Tags</h2>
          <div class="detail__tags-list">
            <div v-for="(tag, index) in bookInfo.tags" :key="index" class="detail__tags-item"
              @click="navigateToTag(tag)">
              {{ tag }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <AdComponent v-if="deviceType == 'mobile'"
      v-show="$global.isObjectNotEmpty(adsensConfig) && adsensConfig?.detail_2" ref="ads-detail-2"
      class="m_padding" :ads="adsensConfig?.detail_2" :showDebug="showDebug" />

    <div class="detail-container-bottom">
      <!-- 章节列表 -->
      <div class="detail__chapters">
        <div class="detail__chapters-header">
          <h2 class="detail__section-title">Chapters</h2>
          <div class="detail__chapters-pagination" v-if="chapterTotalPages > 1">
            <img src="@/assets/vector-left.svg" @click="prevChapterPage"
              :class="{ 'disabled': chapterPage === 1 }">
            <div class="dots">
              <span v-for="page in chapterTotalPages" :key="page" class="dot"
                :class="{ 'active': page === chapterPage }" @click="goToChapterPage(page)"></span>
            </div>
            <img src="@/assets/vector-right.svg" @click="nextChapterPage"
              :class="{ 'disabled': chapterPage >= chapterTotalPages }">
          </div>
        </div>
        <div class="detail__chapters-list" :class="{'skeleton-container': loading}">
          <template v-for="(chapter,index) in displayedChapters">
            <a :key="chapter.id" @click="goToChapter(chapter)" class="detail__chapter-item">
              <div class="chapter-item__number">{{ String(chapter.number).padStart(2, '0') }}</div>
              <div class="chapter-item__title">{{ chapter.title }}</div>
            </a>
            <AdComponent v-if="deviceType == 'mobile' && index == 7"
              v-show="$global.isObjectNotEmpty(adsensConfig) && adsensConfig?.detail_3"
              ref="ads-detail-3" class="" :ads="adsensConfig?.detail_3" :showDebug="showDebug" />
          </template>

        </div>
      </div>
    </div>

    <!-- You may also like -->
    <div class="detail__recommendations-wrapper">
      <div class="detail__recommendations">
        <div class="detail__recommendations-header">
          <h3 class="detail__section-title">You may also like</h3>
        </div>
        <div class="detail__recommendations-list" :class="{'skeleton-container': loadingReleated}">
          <BookCard v-for="book in paginatedReleatedBooks" :key="book.id" :book="book"
            layout="cover-hover" @click="goToBook(book)" />
        </div>
        <div class="detail__recommendations-footer" v-if="totalReleatedPages > 1">
          <div class="detail__recommendations-pagination">
            <img src="@/assets/vector-left.svg" @click="prevReleatedPage"
              :class="{ 'disabled': releatedCurrentPage === 1 }">
            <div class="dots">
              <span v-for="page in totalReleatedPages" :key="page" class="dot"
                :class="{ 'active': page === releatedCurrentPage }"
                @click="goToReleatedPage(page)"></span>
            </div>
            <img src="@/assets/vector-right.svg" @click="nextReleatedPage"
              :class="{ 'disabled': releatedCurrentPage === totalReleatedPages }">
          </div>
          <router-link to="/rankings" class="detail__recommendations-more">
            <span>More Books</span>
            <img src="@/assets/more-icon.svg" alt="More">
          </router-link>
        </div>
      </div>
    </div>

  </CommonPages>
</template>

<script>
import common from '@/mixin/common.js'
import adMixin from '@/mixin/adMixin.js'
import adTrackingMixin from '@/mixin/adTrackingMixin.js'
import AdComponent from '@/pages/components/AdComponent.vue'
import BookCard from '@/components/BookCard.vue'
import { mapGetters } from 'vuex'
import CommonPages from '@/pages/components/page_common.vue'

import '@/css/detail.scss'

export default {
  name: 'Detail',
  components: {
    CommonPages,
    AdComponent,
    BookCard
  },
  mixins: [common, adMixin, adTrackingMixin],
  props: {
    categories: {
      type: String,
      default: ''
    },
    novelId: {
      type: String,
      default: ''
    },
    id: {
      type: [String, Number],
      default: ''
    }
  },
  data () {
    return {
      loading: true,
      defaultCover: require('@/assets/book_default_1.jpg'),
      bookInfo: {
        title: '',
        author: '',
        category: '',
        score: '',
        updateTime: '',
        chapters: '',
        views: '',
        marked: '',
        rank: '',
        cover: '',
        summary: '',
        tags: [],
        chapterList: []
      },
      chapterPage: 1,
      chaptersPerPage: 20,
      releatedCurrentPage: 1,
      releatedPerPage: 6,
      releatedBook: [],
      loadingReleated: true,
      errorMessage: '',
      showCoverChapter: false,
      adConfig: {
        ins: {
          detail_2: 'detail_2',
        },
        releatedBook: 'releatedBook',
      }
    }
  },
  computed: {
    ...mapGetters([
      "adsensConfig",
      'adPosConfig',
      'deviceType',
      'domainConfig',
    ]),
    displayedChapters () {
      const filteredChapters = this.showCoverChapter
        ? this.bookInfo.chapterList
        : this.bookInfo.chapterList.filter(chapter =>
          !(chapter.number === 0 || chapter.number === '0') ||
          !chapter.title.toLowerCase().includes('cover'));

      const start = (this.chapterPage - 1) * this.chaptersPerPage;
      const end = start + this.chaptersPerPage;
      return filteredChapters.slice(start, end);
    },
    chapterTotalPages () {
      const filteredLength = this.showCoverChapter
        ? this.bookInfo.chapterList.length
        : this.bookInfo.chapterList.filter(chapter =>
          !(chapter.number === 0 || chapter.number === '0') ||
          !chapter.title.toLowerCase().includes('cover')).length;

      return Math.ceil(filteredLength / this.chaptersPerPage);
    },
    paginatedReleatedBooks () {
      if (!this.releatedBook || this.releatedBook.length === 0) {
        return [];
      }
      const start = (this.releatedCurrentPage - 1) * this.releatedPerPage;
      const end = start + this.releatedPerPage;
      return this.releatedBook.slice(start, end);
    },
    totalReleatedPages () {
      if (!this.releatedBook || this.releatedBook.length === 0) {
        return 0;
      }
      return Math.ceil(this.releatedBook.length / this.releatedPerPage);
    }
  },
  async activated () {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Detail] Component activated, id: ${this.id}, categories: ${this.categories}, novelId: ${this.novelId}`)
    }
    this.fetchBookDetail();
  },
  async mounted () {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Detail] Component mounted`)
    }
    await this.fetchBookDetail();
    // await this.initAdSystem();
    // this.$nextTick(() => {
    //   this.initAdTracking();
    // });
  },
  deactivated () {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Detail] Component deactivated`)
    }
  },
  beforeDestroy () {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Detail] Component beforeDestroy`)
    }
  },
  methods: {
    async fetchBookDetail () {
      this.loading = true;
      if (!this.id) {
        this.errorMessage = 'Error code 3';
        this.loading = false;
        return;
      }
      try {
        const response = await this.$global.server.getNovalInfo({ id: this.id });
        if (response && response.code === 200 && response.data) {
          const data = response.data.novelInfo;
          this.bookInfo = {
            id: this.id,
            title: data.novelName,
            author: data.novelAuthorName,
            novelId: data.novelId,
            category: data.novelType,
            score: data.novelScoreStr,
            updateTime: data.createTime ? this.$global.formatDate(data.createTime, 'yyyy-MM-dd') : '',
            cover: data.bigImg,
            summary: data.novelStoryIntro,
            tags: data.novelTag ? data.novelTag.split(',').map(tag => tag.trim()) : [],
            views: data.viewCount || 0,
            marked: data.collectCount || 0,
            rank: data.rank || '',
            status: data.status || '',
            chapters: data.novelChapter ? data.novelChapter.length : 0
          };
          this.bookInfo.chapterList = this.formatChapterList(data.novelChapter || []);
          this.releatedBook = (response.data.novelList || []).map(v => ({
            id: v.id,
            title: v.novelName,
            author: v.novelAuthorName,
            coverImage: v.bigImg,
            category: v.novelType,
            rating: v.novelScoreStr,
            novelId: v.novelId
          }));
          this.loadingReleated = false
        } else {
          this.errorMessage = response?.msg || 'Error code 1';
        }
      } catch (error) {
        this.errorMessage = `Error code 2 ${error.message}`;
      }
      this.loading = false;
    },
    formatChapterList (chapters) {
      if (!chapters || !Array.isArray(chapters) || chapters.length === 0) {
        return [];
      }
      const formattedChapters = chapters.map((chapter, index) => {
        if (typeof chapter === 'string') {
          const match = chapter.match(/^(\d+)\.(.+)$/);
          if (match) {
            return { id: index, number: parseInt(match[1], 10), title: match[2].trim() };
          } else {
            return { id: index, number: index + 1, title: chapter };
          }
        } else if (typeof chapter === 'object' && chapter !== null) {
          return { id: chapter.id || index, number: chapter.number || index + 1, title: chapter.title || `Chapter ${index + 1}` };
        } else {
          return { id: index, number: index + 1, title: `Chapter ${index + 1}` };
        }
      });
      if (formattedChapters.some(c => (c.number === 0 || c.number === '0') && c.title.toLowerCase().includes('cover'))) {
        this.bookInfo.chapters = formattedChapters.length - 1;
      }
      return formattedChapters;
    },
    startReading () {
      const firstChapter = this.bookInfo.chapterList.find(c => c.number > 0) || { number: 1 }
      this.goToChapter(firstChapter);
    },
    navigateToTag (tag) {
      // Maybe navigate to categories page with tag as filter
    },
    goToChapter (chapter) {
      const formattedNovelName = this.novelId || this.bookInfo.title.toLowerCase().replace(/\s+/g, '-');
      const summaryId = this.bookInfo.id || this.id;
      this.$router.push(`/chapter/${formattedNovelName}/${summaryId}/${chapter.number}`);
    },
    goToBook (book) {
      const categoryParam = book.category.toLowerCase().replace(/\s+/g, '-');
      const titleParam = book.novelId ? book.novelId.toLowerCase().replace(/\s+/g, '-') :
        book.title.toLowerCase().replace(/\s+/g, '-');
      const idParam = book.id;
      if (this.$route.params.channel) {
        this.$router.push(`/${this.$route.params.channel}/${categoryParam}/${titleParam}/${idParam}`);
      } else {
        this.$router.push(`/${categoryParam}/${titleParam}/${idParam}`);
      }
    },
    prevChapterPage () {
      if (this.chapterPage > 1) this.chapterPage--;
    },
    nextChapterPage () {
      if (this.chapterPage < this.chapterTotalPages) this.chapterPage++;
    },
    goToChapterPage (page) {
      this.chapterPage = page;
    },
    toggleCoverChapter () {
      this.showCoverChapter = !this.showCoverChapter;
      this.chapterPage = 1;
    },
    nextReleatedPage () {
      if (this.releatedCurrentPage < this.totalReleatedPages) {
        this.releatedCurrentPage++;
      }
    },
    prevReleatedPage () {
      if (this.releatedCurrentPage > 1) {
        this.releatedCurrentPage--;
      }
    },
    goToReleatedPage (page) {
      this.releatedCurrentPage = page;
    }
  }
}
</script>

<style lang="scss">
.detail__chapters-cover-toggle {
	margin-left: 10px;
	font-size: 14px;
	display: inline-flex;
	align-items: center;
	input {
		margin-right: 5px;
	}
	span {
		font-size: 14px;
		color: #999;
	}
}
</style>
